import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Container, Paper, TextField, Typography, Alert,
  Grid, MenuItem, FormControl, InputLabel, Select, CircularProgress,
  Chip, OutlinedInput, useTheme, useMediaQuery, Divider
} from '@mui/material';
import dayjs from 'dayjs';
import api from '../../config/axios';

// Template data with images
const templates = [
  { id: 1, name: 'Template 1', image: '/templates/template1.png' },
  { id: 2, name: 'Template 2', image: '/templates/template2.png' },
  { id: 3, name: 'Template 3', image: '/templates/template3.png' },
  { id: 4, name: 'Template 4', image: '/templates/template4.png' }
];

const schema = yup.object().shape({
  product_name: yup.array().min(1, 'At least one product is required'),
  courier_id: yup.string().required('Courier partner is required'),
  phone: yup.string().required('Phone is required'),
  email: yup.string().email('Invalid email').optional(),
  dom: yup.date().required('Date of Marriage is required'),
  bride_name: yup.string().required('Bride name is required'),
  groom_name: yup.string().required('Groom name is required'),
  display_bride_name: yup.string().required('Display bride name is required'),
  display_groom_name: yup.string().required('Display groom name is required'),
  short_display_name: yup.string().required('Short display name is required'),
  advance_amount: yup.number().required('Advance amount is required'),
  order_amount: yup.number().required('Order amount is required'),
  profit: yup.number().required('Profit is required'),
  remark: yup.string().optional(),
});

const CreateOrder = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const isEditMode = !!orderId;

  const [apiError, setApiError] = React.useState('');
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const { register, handleSubmit, formState: { errors, isSubmitting }, setValue, watch } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      product_name: [],
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsResponse, couriersResponse] = await Promise.all([
          api.get('/products.php'),
          api.get('/courier_partners.php')
        ]);
        setProducts(productsResponse.data.data || []);
        setCouriers(couriersResponse.data.data || []);

        // If in edit mode, fetch order data
        if (isEditMode) {
          const orderResponse = await api.get(`/orders.php?id=${orderId}`);
          if (orderResponse.data.success && orderResponse.data.data.order) {
            const order = orderResponse.data.data.order;
            setOrderData(order);

            // Pre-fill form with order data
            // Handle product_name as array - parse if it's a string, or use as array
            let productIds = [];
            if (order.product_name) {
              if (typeof order.product_name === 'string') {
                try {
                  productIds = JSON.parse(order.product_name);
                } catch (e) {
                  // If parsing fails, treat as single ID
                  productIds = [order.product_name];
                }
              } else if (Array.isArray(order.product_name)) {
                productIds = order.product_name;
              } else {
                productIds = [order.product_name.toString()];
              }
            }
            setValue('product_name', productIds);
            setValue('courier_id', order.courier_id?.toString() || '');
            setValue('phone', order.phone || '');
            setValue('email', order.email || '');
            setValue('dom', order.dom ? new Date(order.dom).toISOString().split('T')[0] : '');
            setValue('bride_name', order.bride_name || '');
            setValue('groom_name', order.groom_name || '');
            setValue('display_bride_name', order.display_bride_name || '');
            setValue('display_groom_name', order.display_groom_name || '');
            setValue('short_display_name', order.short_display_name || '');
            setValue('advance_amount', order.advance_amount || '');
            setValue('order_amount', order.order_amount || '');
            setValue('profit', order.profit || '');
            setValue('remark', order.remark || '');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setApiError('Failed to load data');
      } finally {
        setFetchLoading(false);
      }
    };

    fetchData();
  }, [orderId, isEditMode, setValue]);

  // Auto-fill logic
  const brideName = watch('bride_name');
  const groomName = watch('groom_name');

  useEffect(() => {
    if (brideName) {
      const firstName = brideName.trim().split(' ')[0];
      setValue('display_bride_name', firstName, { shouldValidate: true, shouldDirty: true });
    } else {
      setValue('display_bride_name', '', { shouldValidate: true, shouldDirty: true });
    }
  }, [brideName, setValue]);

  useEffect(() => {
    if (groomName) {
      const firstName = groomName.trim().split(' ')[0];
      setValue('display_groom_name', firstName, { shouldValidate: true, shouldDirty: true });
    } else {
      setValue('display_groom_name', '', { shouldValidate: true, shouldDirty: true });
    }
  }, [groomName, setValue]);

  useEffect(() => {
    if (brideName && groomName) {
      const brideInitial = brideName.trim().charAt(0).toUpperCase();
      const groomInitial = groomName.trim().charAt(0).toUpperCase();
      setValue('short_display_name', `${groomInitial} & ${brideInitial}`, { shouldValidate: true, shouldDirty: true });
    } else {
      setValue('short_display_name', '', { shouldValidate: true, shouldDirty: true });
    }
  }, [brideName, groomName, setValue]);

  const onSubmit = async (data) => {
    setApiError('');
    setLoading(true);
    try {
      // Stringify product_name array before sending
      const formData = {
        ...data,
        dom: dayjs(data?.dom).format("YYYY-MM-DD"),
        product_name: JSON.stringify(data.product_name)
      };
      let response;
      if (isEditMode) {
        response = await api.put(`/orders.php?id=${orderId}`, formData);
      } else {
        response = await api.post('/orders.php', formData);
      }

      if (response.data.success) {
        navigate('/orders');
      } else {
        setApiError(response.data.message || `Failed to ${isEditMode ? 'update' : 'create'} order`);
      }
    } catch (err) {
      console.log(err);
      setApiError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} order`);
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, px: { xs: 2, md: 4 } }}>
      <Box
        sx={{
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.05)',
          p: { xs: 3, md: 6 },
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 900,
              color: 'text.primary',
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              letterSpacing: '-0.01em',
              mb: 0.5
            }}
          >
            {isEditMode ? 'Update Order Details' : 'Create New Order'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {isEditMode ? 'Modify existing order information' : 'Register a new boutique order'}
          </Typography>
        </Box>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid
            container
            spacing={2}
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: 2,
            }}
          >
            {/* Section 1: Logistics & Contact */}
            <Grid item xs={12} sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Logistics & Contact
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.product_name}>
                <InputLabel>Products</InputLabel>
                <Select
                  multiple
                  value={watch('product_name') || []}
                  onChange={(e) => setValue('product_name', e.target.value)}
                  input={<OutlinedInput label="Products" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const product = products.find(p => p.id.toString() === value.toString() || p.name === value);
                        return (
                          <Chip
                            key={value}
                            label={product ? product.name : value}
                            size="small"
                            onDelete={() => {
                              const current = watch('product_name') || [];
                              setValue('product_name', current.filter(item => item !== value));
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {products.map(product => (
                    <MenuItem key={product.id} value={product.name.toString()}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.product_name && (
                  <Typography color="error" variant="caption">
                    {errors.product_name.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.courier_id}>
                <InputLabel>Courier Partner</InputLabel>
                <Select
                  label="Courier Partner"
                  {...register('courier_id')}
                  defaultValue=""
                >
                  {couriers.map(courier => (
                    <MenuItem key={courier.id} value={courier.id}>
                      {courier.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.courier_id && (
                  <Typography color="error" variant="caption">
                    {errors.courier_id.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Phone"
                {...register('phone')}
                fullWidth
                placeholder="Enter Whatsapp number"
                error={!!errors.phone}
                helperText={errors.phone?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email (optional)"
                {...register('email')}
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            </Grid>

            {/* Section 2: Couple Details */}
            <Grid item xs={12} sx={{ mt: 3, gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Couple Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Date Of Marriage"
                {...register('dom')}
                fullWidth
                error={!!errors.dom}
                helperText={errors.dom?.message}
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Bride Full Name"
                {...register('bride_name')}
                fullWidth
                error={!!errors.bride_name}
                helperText={errors.bride_name?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Groom Full Name"
                {...register('groom_name')}
                fullWidth
                error={!!errors.groom_name}
                helperText={errors.groom_name?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Display Bride Name"
                {...register('display_bride_name')}
                fullWidth
                error={!!errors.display_bride_name}
                helperText={errors.display_bride_name?.message}
                InputLabelProps={{ shrink: !!watch('display_bride_name') }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Display Groom Name"
                {...register('display_groom_name')}
                fullWidth
                error={!!errors.display_groom_name}
                helperText={errors.display_groom_name?.message}
                InputLabelProps={{ shrink: !!watch('display_groom_name') }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Short Display Name"
                {...register('short_display_name')}
                fullWidth
                error={!!errors.short_display_name}
                helperText={errors.short_display_name?.message}
                InputLabelProps={{ shrink: !!watch('short_display_name') }}
              />
            </Grid>

            {/* Section 3: Financial Details */}
            <Grid item xs={12} sx={{ mt: 3, gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Financial Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Advance Amount"
                {...register('advance_amount')}
                fullWidth
                error={!!errors.advance_amount}
                helperText={errors.advance_amount?.message}
                type="number"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Order Amount"
                {...register('order_amount')}
                fullWidth
                error={!!errors.order_amount}
                helperText={errors.order_amount?.message}
                type="number"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Profit"
                {...register('profit')}
                fullWidth
                error={!!errors.profit}
                helperText={errors.profit?.message}
                type="number"
              />
            </Grid>

            {/* Section 4: Remarks */}
            <Grid item xs={12} sx={{ mt: 3, gridColumn: '1 / -1' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Remarks
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sx={{ gridColumn: '1 / -1' }}>
              <TextField
                label="Remark"
                {...register('remark')}
                fullWidth
                multiline
                rows={4}
                placeholder="Any special instructions or notes..."
                error={!!errors.remark}
                helperText={errors.remark?.message}
              />
            </Grid>
            {/* <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.courier_id}>
                <InputLabel>Courier Partner</InputLabel>
                <Select
                  label="Courier Partner"
                  {...register('courier_id')}
                  defaultValue="shiprocket"
                >
                  {couriers.map(courier => (
                    <MenuItem key={courier.id} value={courier.id.toString()}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography>{courier.name}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.courier_id && (
                  <Typography color="error" variant="caption">
                    {errors.courier_id.message}
                  </Typography>
                )}
              </FormControl>
            </Grid> */}
          </Grid>
          {apiError && <Alert severity="error" sx={{ mt: 2 }}>{apiError}</Alert>}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              mt: 6,
              py: 2,
              borderRadius: '14px',
              fontWeight: 800,
              fontSize: '1rem',
              background: 'linear-gradient(135deg, #B51D6E 0%, #E91E63 100%)',
              color: "#FFFFFF !important",
              boxShadow: '0 8px 25px rgba(181, 29, 110, 0.25)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #E91E63 0%, #B51D6E 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 35px rgba(181, 29, 110, 0.35)',
              },
              '&:disabled': {
                background: 'rgba(0,0,0,0.1)',
                color: 'text.disabled',
              }
            }}
            disabled={isSubmitting || loading}
          >
            {isSubmitting || loading ? (isEditMode ? 'Finalizing Updates...' : 'Creating Memory...') : (isEditMode ? 'Update Order' : 'Create Order')}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default CreateOrder;
